"use client";

import { Mail, MapPinned, PhoneCall } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import toast from "react-hot-toast";

const ContactUsMain = () => {
  const [formData, setFormData] = useState({
    title: "Mr",
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [responseMsg, setResponseMsg] = useState("");

  // ✅ Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  // ✅ Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Enter a valid email";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    else if (!/^\d{10}$/.test(formData.phone))
      newErrors.phone = "Enter a valid 10-digit number";
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setResponseMsg("");

    const payload = {
      cityId: "",
      propertyId: "",
      firstName: [formData.title, formData.name].filter(Boolean).join(" ").trim(),
      lastName: "",
      mobileNo: formData.phone,
      email: formData.email,
      eventDate: "",
      eventType: "Contact",
      roomsRequired: false,
      noOfRooms: "0",
      noOfGuests: "0",
      checkIn: "",
      checkOut: "",
      requestFrom: "Contact Page",
      remarks: formData.message,
      remarks1: formData.subject,
      remarks2: "",
    };

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL}Api/common/EnquireNow`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      let data = null;
      try {
        data = await res.clone().json();
      } catch {}

      if (!res.ok) {
        const msg = data?.message || `HTTP ${res.status}`;
        throw new Error(msg);
      }

      if (data?.errorCode && String(data.errorCode) !== "0") {
        throw new Error(data.errorMessage || "Something went wrong");
      }

      toast.success("Your enquiry has been submitted successfully!");
      setResponseMsg("Enquiry submitted successfully!");
      setFormData({
        title: "Mr",
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to send enquiry. Please try again later.");
      setResponseMsg("Failed to send enquiry. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="contact-us-2 contact-us-main">
        <div className="container contact-us-main-form">
          {/* ===== CONTACT DETAILS + MAP ===== */}
          <div className="row mb-3 mt-4">
            <div className="col-lg-4 col-md-4">
              <div className="address-contact mb-3">
                <h3 className="inner-head-title fw-semibold">Address</h3>
                <div className="box-d-flex">
                  <MapPinned />
                  <p>
                    <Link
                      href="https://maps.app.goo.gl/4FA9tJiAiU4uhqhr9"
                      target="_blank"
                      className="linkk-unstyled text-black"
                    >
                      302, 3rd Floor, Mewad,
                      <br /> Patanwala Industrial Estate,
                      <br />
                      LBS Marg, Ghatkopar ( West),
                      <br /> Mumbai, Maharashtra 400086
                    </Link>
                  </p>
                </div>
              </div>

              <div className="address-contact mb-3">
                <h3 className="inner-head-title fw-semibold">Email</h3>
                <div className="box-d-flex">
                  <Mail />
                  <p>
                    <Link
                      href="mailto:reservations@orangetigerhotels.com"
                      className="linkk-unstyled text-black"
                    >
                      reservations@orangetigerhotels.com
                    </Link>
                  </p>
                </div>
                <div className="box-d-flex">
                  <Mail />
                  <p>
                    <Link
                      href="mailto:mail@orangetigerhotels.com"
                      className="linkk-unstyled text-black"
                    >
                      mail@orangetigerhotels.com
                    </Link>
                  </p>
                </div>
                <div className="box-d-flex">
                  <Mail />
                  <p>
                    <Link
                      href="mailto:rmd@orangetigerhotels.com"
                      className="linkk-unstyled text-black"
                    >
                      rmd@orangetigerhotels.com
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <div className="col-lg-8 col-md-8">
              <iframe
                className="map-iframe"
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d60325.20515607883!2d72.915563!3d19.093379!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c74a360fcbb1%3A0xda301c57fe67066!2sOrange%20Tiger%20Hospitality%20Private%20Limited.%20(OTHPL)!5e0!3m2!1sen!2sin!4v1761813820844!5m2!1sen!2sin"
                width="100%"
                height="300"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                title="Orange Tiger Hospitality location map"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* <div className="bg-gray-50 p-4 shadow col-span-1 md:col-span-2 mt-4">
            <h3 className="text-xl fw-semibold mb-2 !text-[20px]">
              Hotel Sales & Revenue
            </h3>
            <p>
              If you wish to find out more about RFPs, group discounts, or
              contractual pricing, contact the corporate or regional offices.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              {[
                {
                  title: "RSO Mumbai",
                  name: "Mr. Ashwin Yadav",
                  email: "ashwin.yadav@orangetigerhotels.com",
                  phone: "+91 8976772950",
                },
                {
                  title: "RSO Delhi",
                  name: "Ms. Sapna Saxena",
                  email: "sapna.saxena@orangetigerhotels.com",
                  phone: "+91 9899888403",
                },
                {
                  title: "RSO Gujarat",
                  name: "Mr. Chirag Desai",
                  email: "sales.gujarat@orangetigerhotels.com",
                  phone: "+91 8976804130",
                },

                {
                  title: "RSO Pune",
                  name: "Mr. Rajkumar Tagram",
                  email: "rajkumar. tagram@orangetigerhotels.com",
                  phone: "+91 89768 04129",
                },
                {
                  title: "RSO Bangalore",
                  name: "Mr. Rakesh Ranjan",
                  email: "sales.bangalore@orangetigerhotels.com",
                  phone: "+91 9967172338",
                },
                {
                  title: "RSO Jaipur",
                  name: "Mr. Sameer Prajapati",
                  email: "sales.jaipur@orangetigerhotels.com",
                  phone: "+91 8824616137",
                },
                {
                  title: "RSO Punjab",
                  name: "Mr. Sandeep Sachdeva",
                  email: "sandeep.sachdeva@orangetigerhotels.com",
                  phone: "+91 9779742202",
                },
                {
                  title: "RSO Hyderabad",
                  name: "Mr. Krishna Mohan Jha",
                  email: "krishna.mohan@orangetigerhotels.com",
                  phone: "+91 8291788796",
                },
                {
                  title: "RSO Kolkata",
                  name: "Mr. Banti Saha",
                  email: "banti.saha@orangetigerhotels.com",
                  phone: "+91 9903956565",
                },
              ].map((office, index) => (
                <div key={index} className="bg-white p-4 border">
                  <h4 className="fw-semibold mb-2 !text-[18px]">
                    {office.title}
                  </h4>
                  <p className="text-sm mb-1">
                    <strong>Name :</strong> {office.name}
                  </p>
                  <p className="text-sm mb-1">
                    <strong>Email :</strong>{" "}
                    <a href={`mailto:${office.email}`} className="text-black">
                      {office.email}
                    </a>
                  </p>
                  <p className="text-sm">
                    <strong>Contact Number :</strong> {office.phone}
                  </p>
                </div>
              ))}
            </div>
          </div> */}

          <div
            className="row justify-content-center pt-4 mt-4"
            style={{
              boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
              borderRadius: "5px",
              fontSize: "12px",
            }}
          >
            <div className="col-md-12 mb-4">
              <form
                onSubmit={handleSubmit}
                className="Query-form-fields"
                noValidate
              >
                {/* ===== FORM FIELDS ===== */}
                <div className="row">
                  <div className="col-lg-6 mb-3">
                   
                    <div className="d-flex gap-2">
                      <select
                        id="contactTitle"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        className="form-select contact-name-title"
                        aria-label="Title"
                      >
                        <option value="Mr">Mr</option>
                        <option value="Ms">Ms</option>
                        <option value="Mrs">Mrs</option>
                        <option value="Dr">Dr</option>
                      </select>

                      <input
                        id="contactName"
                        type="text"
                        name="name"
                        maxLength={30}
                        placeholder="Name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                      />
                    </div>
                    {errors.name && (
                      <small className="text-danger">{errors.name}</small>
                    )}
                  </div>

                  <div className="col-lg-6 mb-3">
                    <input
                      type="email"
                      name="email"
                      placeholder="Email"
                      maxLength={100}
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className={`form-control ${
                        errors.email ? "is-invalid" : ""
                      }`}
                    />
                    {errors.email && (
                      <small className="text-danger">{errors.email}</small>
                    )}
                  </div>

                  <div className="col-lg-6 mb-3">
                    <input
                      type="tel"
                      maxLength={10}
                      name="phone"
                      placeholder="Phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className={`form-control ${
                        errors.phone ? "is-invalid" : ""
                      }`}
                    />
                    {errors.phone && (
                      <small className="text-danger">{errors.phone}</small>
                    )}
                  </div>

                  <div className="col-lg-6 mb-3">
                    <input
                      type="text"
                      name="subject"
                      placeholder="Subject"
                      required
                      value={formData.subject}
                      maxLength={500}
                      onChange={handleChange}
                      className={`form-control ${
                        errors.subject ? "is-invalid" : ""
                      }`}
                    />
                    {errors.subject && (
                      <small className="text-danger">{errors.subject}</small>
                    )}
                  </div>

                  <div className="col-lg-12 mb-3">
                    <textarea
                      name="message"
                      placeholder="Message"
                      maxLength={500}
                      required
                      value={formData.message}
                      onChange={handleChange}
                      className={`form-control ${
                        errors.message ? "is-invalid" : ""
                      }`}
                    />
                    {errors.message && (
                      <small className="text-danger">{errors.message}</small>
                    )}
                  </div>
                </div>

                {/* ===== SUBMIT BUTTON ===== */}
                <div className="text-center">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary contact-submit mt-3 !text-[12px]"
                  >
                    {loading ? "Processing..." : "Submit"}
                  </button>

                  {responseMsg && (
                    <p className="mt-2 text-success fw-bold">{responseMsg}</p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactUsMain;
